import { keepPreviousData } from '@tanstack/react-query'
import { ChevronDownIcon, ChevronUpIcon, Sparkles } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useClearPlanFilters } from 'components/hooks/member_onboarding_v2/useClearPlanFilters'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Banner, DropdownMenu, TextButton, TextLink } from 'slab'
import {
  useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlans as useGetMedicalPlans,
  usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdEnrollmentsEnroll as useCreateEnrollment,
  usePostMemberOnboardingPlanSelectionPreferencesCreateOrUpdate as useCreateOrEditPlanSelectionPreference,
} from 'utils/web_api'
import {
  type GetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansExpectedPlanUtilization as ExpectedPlanUtilization,
  type GetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansMetalTierItem as MetalTier,
  type GetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansPlanTypeItem as PlanType,
  type GetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansSortItem as SortItem,
  GetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansSortItem as SortItemValues,
  type MemberOnboardingMedicalPlanDisplayItem,
  type MemberOnboardingMedicalPlanDisplayItemList
} from 'utils/web_api.schemas'

import { BorderedBanner } from '../../members/components/BorderedBanner/BorderedBanner'
import { MedicalPlanCard } from '../components/MedicalPlanCard'
import { OnboardingGenericErrorMessage } from '../components/OnboardingGenericErrorMessage'
import { PageErrorBanner } from '../components/PageErrorBanner'
import { PlanComparisonSidebarWidget } from '../components/PlanComparisonList'
import {
  PlanGranularFilters,
  type PlanGranularFiltersTypes,
} from '../components/PlanGranularFilters'
import {
  RecommendationQuestionBar,
  type RecommendationSearchFilters,
} from '../components/RecommendationQuestionBar'
import { type MedicalPlanDisplayItem } from '../medicalPlanTypes'
import { useOnboarding } from '../OnboardingContext'
import { type StepComponentProps } from '../types'
import { getLabeledPlans, showThisRecommendedPlan } from '../utils'
import {
  getStoredPlansForComparison,
  storePlansForComparison,
} from '../utils/planComparisonStorage'

type MedicalPlansPageProps = StepComponentProps & {
  setViewingOffThatch: (viewingOffThatch: boolean) => void;
}

type PlanSelectionPreferences = {
  expectedPlanUtilization?: ExpectedPlanUtilization | null;
  oopBudget?: number | null;
  providers?: object[] | null;
} | undefined

type SortOption = {
  label: string;
  sortArray: SortItem[];
}

// Single source of truth for sort options
const SORT_OPTIONS: SortOption[] = [
  {
    label: 'Recommended',
    sortArray: ['bucketed_premium', 'deductible', '-provider_count'],
  },
  {
    label: 'Lowest deductible',
    sortArray: ['deductible', 'premium', '-actuarial_value'],
  },
  {
    label: 'Highest coverage',
    sortArray: ['-actuarial_value', 'deductible', 'premium'],
  },
]

const DEFAULT_SORT = SORT_OPTIONS[0]!

/**
 * Compares two sort arrays to see if they are equal.
 */
const areSortArraysEqual = (
  a: SortItem[],
  b: SortItem[]
): boolean => {
  return (
    a.length === b.length &&
    a.every((value, index) => value === b[index])
  )
}

/**
 * Finds the sort option that matches the given sort array.
 * Returns a custom sort option if no match is found.
 */
const findSortOption = (sortArray: SortItem[]): SortOption => {
  return (
    SORT_OPTIONS.find((option) =>
      areSortArraysEqual(option.sortArray, sortArray)
    ) || {
      label: 'Custom',
      sortArray,
    }
  )
}

/**
 * Checks if a sort array matches the default sort.
 */
const isDefaultSort = (sortArray: SortItem[]): boolean => {
  return areSortArraysEqual(sortArray, DEFAULT_SORT.sortArray)
}

/**
 * Gets filter names from the medical plan data. Returns stale data when the
 * medical plan data is refetching to ensure filtering interfaces that use this
 * data don't flash while the plans are being refetched.
 */
const useFilterNames = (medicalPlanData?: MemberOnboardingMedicalPlanDisplayItemList) => {
  const getFilterNames = useCallback(() => {
    return {
      carriers: medicalPlanData?.filters?.carriers || [],
      metalTiers: (medicalPlanData?.filters?.metal_tiers || []) as MetalTier[],
      planTypes: (medicalPlanData?.filters?.plan_types || []) as PlanType[],
    }
  }, [medicalPlanData])

  const ref = useRef(getFilterNames())

  if (medicalPlanData?.filters) {
    ref.current = getFilterNames()
  }

  return ref.current
}

export const useFilteredPlans = (plansForComparisonIds?: string[]): {
  availablePlans: MemberOnboardingMedicalPlanDisplayItem[];
  totalPlans: number;
  lowestCostPlan?: MemberOnboardingMedicalPlanDisplayItem;
  mostComprehensivePlan?: MemberOnboardingMedicalPlanDisplayItem;
  renewalPlan?: MemberOnboardingMedicalPlanDisplayItem;
  recommendationNotAvailable: boolean;
  planFilters: PlanGranularFiltersTypes;
  filterNames: {
    carriers: string[];
    metalTiers: MetalTier[];
    planTypes: PlanType[];
  };
  setPlanFilters: React.Dispatch<React.SetStateAction<PlanGranularFiltersTypes>>;
  setPlanSelectionPreferences: React.Dispatch<React.SetStateAction<PlanSelectionPreferences>>;
  medicalPlanLoading: boolean;
  medicalPlanFetching: boolean;
  medicalPlanError?: GrapeAPIErrorType | null;
  sortArray: SortItem[];
  plansForComparison?: MemberOnboardingMedicalPlanDisplayItem[];
} => {
  const { onboardingData, planChangeSummary, enrollmentEventId } = useOnboarding()
  const [planFilters, setPlanFilters] = useState<PlanGranularFiltersTypes>(
    () => {
      // Initialize from URL parameters
      const params = new URLSearchParams(window.location.search)
      return {
        metalTiers: (params.get('metal_tiers')?.split(',') ||
          []) as MetalTier[],
        carriers: params.get('carriers')?.split(',') || [],
        planTypes: (params.get('plan_types')?.split(',') || []) as PlanType[],
      }
    }
  )

  useClearPlanFilters()

  const [planSelectionPreferences, setPlanSelectionPreferences] = useState<PlanSelectionPreferences>()

  // Track URL search string to handle URL changes (browser back/forward navigation)
  const [urlSearch, setUrlSearch] = useState(window.location.search)
  const urlSearchRef = useRef(window.location.search)

  // Listen for browser navigation (back/forward) to update URL state
  useEffect(() => {
    const handlePopState = () => {
      const currentSearch = window.location.search
      urlSearchRef.current = currentSearch
      setUrlSearch(currentSearch)
    }
    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  // Also check for programmatic URL changes (e.g., via pushState)
  // This ensures we catch URL updates that don't trigger popstate events
  useEffect(() => {
    const handleUrlChange = () => {
      const currentSearch = window.location.search
      if (currentSearch !== urlSearchRef.current) {
        urlSearchRef.current = currentSearch
        setUrlSearch(currentSearch)
      }
    }

    // Check on every render
    handleUrlChange()

    // Also listen for custom events that signal URL changes
    window.addEventListener('urlchange', handleUrlChange)

    return () => {
      window.removeEventListener('urlchange', handleUrlChange)
    }
  })

  const validSortItems = new Set(Object.values(SortItemValues))

  const sortArray = useMemo(() => {
    const params = new URLSearchParams(urlSearch)
    const sortParam = params.get('sort')
    if (sortParam) {
      // Handle comma-separated values and validate against allowed values
      const parsed = sortParam
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .filter((value): value is SortItem => validSortItems.has(value as SortItem))

      // If any valid values were found, return them
      if (parsed.length > 0) {
        return parsed
      }
    }
    // Otherwise, return the default sort
    return DEFAULT_SORT.sortArray
  }, [urlSearch])

  // Check if 'recommend' query parameter is set to true
  const generateRecommendations = useMemo(() => {
    const params = new URLSearchParams(urlSearch)
    const recommendParam = params.get('recommend')
    // Generate recommendations if 'recommend' is explicitly set to 'true' or if the sort is the default sort
    return (
      recommendParam?.toLowerCase() === 'true' || isDefaultSort(sortArray)
    )
  }, [urlSearch, sortArray])

  const {
    data: medicalPlanData,
    isLoading: medicalPlanLoading,
    isFetching: medicalPlanFetching,
    error: medicalPlanError,
  } = useGetMedicalPlans<MemberOnboardingMedicalPlanDisplayItemList, GrapeAPIErrorType>(
    enrollmentEventId,
    {
      expected_plan_utilization:
        planSelectionPreferences?.expectedPlanUtilization || undefined,
      oop_budget: planSelectionPreferences?.oopBudget ?? undefined,
      'sort[]': sortArray,
      'metal_tier[]':
        planFilters.metalTiers.length > 0 ? planFilters.metalTiers : undefined,
      'plan_type[]':
        planFilters.planTypes.length > 0 ? planFilters.planTypes : undefined,
      'carrier_name[]':
        planFilters.carriers.length > 0 ? planFilters.carriers : undefined,
      'provider_npis[]':
        planSelectionPreferences?.providers?.map(
          (provider) => (provider as any).npi
        ) || undefined,
      generate_recommendations: generateRecommendations,
      'plans_for_comparison[]':
        plansForComparisonIds && plansForComparisonIds.length > 0
          ? plansForComparisonIds
          : undefined,
      // TODO (next PR): infinite scroll, or no pagination
      'page[number]': 1,
      'page[size]': 1000,
    },
    {
      query: {
        enabled: !!onboardingData,
        retry: false,
        placeholderData: keepPreviousData,
        queryKey: [
          'enrollmentEvent',
          enrollmentEventId,
          'medical_plans',
          planSelectionPreferences?.expectedPlanUtilization,
          planSelectionPreferences?.oopBudget,
          planFilters.metalTiers,
          planFilters.planTypes,
          planFilters.carriers,
          planSelectionPreferences?.providers,
          sortArray,
          generateRecommendations,
          plansForComparisonIds,
        ]
      },
    }
  )

  const filterNames = useFilterNames(medicalPlanData)

  const availablePlans = useMemo(() => medicalPlanData?.data ?? [], [medicalPlanData?.data])
  const lowestCostPlan =
    medicalPlanData?.recommended_plans?.lowest_cost_plan_display_item
  const mostComprehensivePlan =
    medicalPlanData?.recommended_plans?.most_comprehensive_plan_display_item
  const renewalPlanId = planChangeSummary?.next_plan_year.medical_plan?.plan_code || null
  let renewalPlan
  if (renewalPlanId) {
    renewalPlan = availablePlans.find((plan) => plan.plan.id === renewalPlanId)
  }
  const totalPlans = medicalPlanData?.pagination?.total_records || 0

  // We don't show recommendations if there are less than 2 plans
  let recommendationNotAvailable = Boolean(
    !medicalPlanLoading &&
    (medicalPlanData?.total_count_without_filters === 0 ||
      (medicalPlanData?.total_count_without_filters &&
        medicalPlanData?.total_count_without_filters < 2))
  )

  // We don't show recommendations if there are only 2 plans and they have the same premium
  if (
    medicalPlanData?.total_count_without_filters === 2 &&
    medicalPlanData?.data?.every(
      (plan, _, arr) =>
        plan.total_premium?.subunits === arr[0].total_premium?.subunits
    )
  ) {
    recommendationNotAvailable = true
  }

  useEffect(() => {
    if (onboardingData?.medical_plan_selection_preferences) {
      setPlanSelectionPreferences((prev) => ({
        ...prev,
        expectedPlanUtilization:
          onboardingData.medical_plan_selection_preferences
            ?.expected_plan_utilization,
        oopBudget:
          onboardingData.medical_plan_selection_preferences?.oop_budget ?? null,
      }))
    }
  }, [onboardingData?.medical_plan_selection_preferences])

  useEffect(() => {
    if (onboardingData?.provider_preferences) {
      setPlanSelectionPreferences((prev) => ({
        ...prev,
        providers: onboardingData.provider_preferences,
      }))
    }
  }, [onboardingData?.provider_preferences])

  return {
    availablePlans,
    totalPlans,
    lowestCostPlan,
    mostComprehensivePlan,
    renewalPlan,
    recommendationNotAvailable,
    planFilters,
    filterNames,
    setPlanFilters,
    setPlanSelectionPreferences,
    medicalPlanLoading,
    medicalPlanFetching,
    medicalPlanError,
    sortArray,
    plansForComparison: medicalPlanData?.plans_for_comparison,
  }
}

// TODO: this isn't a page anymore, it's a component and should be moved and renamed accordingly
export function MedicalPlansPage ({
  navigateToNextStep,
  navigateToSpecificStep,
  setViewingOffThatch,
}: MedicalPlansPageProps) {
  const { onboardingData, enrollmentEventId, showSmartBudget, nvIncorrectPlanPricesBanner } = useOnboarding()

  const [submitError, setSubmitError] = useState<GrapeAPIErrorType | null>(
    null
  )
  const [selectedPlansForComparison, setSelectedPlansForComparison] = useState<
    MedicalPlanDisplayItem[]
  >(() => getStoredPlansForComparison())

  const plansForComparisonIds = useMemo(
    () => selectedPlansForComparison.map((plan) => plan.plan.id),
    [selectedPlansForComparison]
  )

  const {
    availablePlans,
    totalPlans,
    lowestCostPlan,
    mostComprehensivePlan,
    renewalPlan,
    recommendationNotAvailable,
    planFilters,
    filterNames,
    setPlanFilters,
    setPlanSelectionPreferences,
    medicalPlanLoading,
    medicalPlanFetching,
    medicalPlanError,
    sortArray,
    plansForComparison: plansForComparisonFromApi,
  } = useFilteredPlans(plansForComparisonIds)

  const getSortLabel = useCallback((sort: SortItem[]): string => {
    return findSortOption(sort).label
  }, [])

  const updateSort = useCallback((newSort: SortItem[]) => {
    const url = new URL(window.location.href)

    if (isDefaultSort(newSort)) {
      // Remove sort param if it's the default
      url.searchParams.delete('sort')
    } else {
      // Set sort param
      url.searchParams.set('sort', newSort.join(','))
    }
    window.history.pushState({}, '', url.toString())
    // Dispatch a custom event to notify useFilteredPlans of the URL change
    window.dispatchEvent(new Event('urlchange'))
  }, [])

  const createOrEditPlanSelectionPreferenceMutation =
    useCreateOrEditPlanSelectionPreference()

  const handleRecommendationSearch = ({
    expectedPlanUtilization,
    oopBudget,
    providers,
  }: RecommendationSearchFilters) => {
    if (onboardingData?.enrollment_event.plan_year) {
      createOrEditPlanSelectionPreferenceMutation.mutate(
        {
          data: {
            // these two fields can be set to 'undefined' when the selection is cleared
            // from the recommendation search bar. In order to set these field values to
            // null from the backend, we need to set them as null explicitly here
            expected_plan_utilization: expectedPlanUtilization || (null as any),
            oop_budget: oopBudget ?? (null as any),
            year: onboardingData?.enrollment_event.plan_year,
          },
        },
        {
          onSettled: () => {
            // Clear filters after recommendation search is complete
            setPlanFilters({
              metalTiers: [],
              carriers: [],
              planTypes: [],
            })
            // Clear URL parameters
            const url = new URL(window.location.href)
            url.searchParams.delete('metal_tiers')
            url.searchParams.delete('carriers')
            url.searchParams.delete('plan_types')
            window.history.replaceState({}, '', url.toString())

            setPlanSelectionPreferences({
              expectedPlanUtilization,
              oopBudget,
              providers,
            })
          },
        }
      )
    }
  }

  const createEnrollment = useCreateEnrollment({
    mutation: {
      onSuccess: () => {
        navigateToNextStep?.()
      },
      onError: (error) => {
        setSubmitError(error as GrapeAPIErrorType)
      },
    },
  })

  const currentPlan =
    onboardingData?.enrollments?.find((e) => e.covers_medical) || null
  const city = onboardingData?.member?.city
  const labeledPlans = getLabeledPlans({
    lowestCostPlan,
    mostComprehensivePlan,
    renewalPlan: renewalPlan || undefined,
  })

  useEffect(() => {
    if (plansForComparisonFromApi) {
      setSelectedPlansForComparison(plansForComparisonFromApi)
      storePlansForComparison(plansForComparisonFromApi)
    }
  }, [plansForComparisonFromApi])

  const handleSelectPlan = (plan: MedicalPlanDisplayItem) => {
    createEnrollment.mutate({
      enrollmentEventId,
      data: {
        covers_medical: true,
        covers_dental: false,
        covers_vision: false,
        plan_code: plan.plan.id,
        carrier_name: plan.plan.carrier_name,
        quoted_premium: (plan.total_premium?.subunits || 0) / 100,
        employee_only_premium:
          (plan.tiered_allowance_employee_only_premium?.subunits || 0) / 100,
      },
    })
  }

  const setShowMedicalPlanDetail = (plan: MedicalPlanDisplayItem) => {
    navigateToSpecificStep?.(`medical_plan_details:${plan.plan.id}`)
  }

  const onSelectMorePlans = () => {
    navigateToSpecificStep?.('in_network_verification')
  }

  const handlePlanComparisonToggle = (
    plan: MedicalPlanDisplayItem,
    isSelected: boolean
  ) => {
    if (isSelected) {
      if (selectedPlansForComparison.length >= 3) {
        return // Don't add more plans if we already have 3
      }
      const newPlans = [...selectedPlansForComparison, plan]
      setSelectedPlansForComparison(newPlans)
      storePlansForComparison(newPlans)
    } else {
      const newPlans = selectedPlansForComparison.filter(
        (p) => p.plan.id !== plan.plan.id
      )
      setSelectedPlansForComparison(newPlans)
      storePlansForComparison(newPlans)
    }
  }

  const handleCompareClick = () => {
    if (selectedPlansForComparison.length > 1) {
      navigateToSpecificStep?.(
        `medical_plan_comparison:${selectedPlansForComparison
          .map((plan) => plan.plan.id)
          .join(',')}`
      )
    } else if (selectedPlansForComparison.length === 1) {
      navigateToSpecificStep?.(
        `medical_plan_details:${selectedPlansForComparison[0].plan.id}`
      )
    }
  }

  const planSelectionCallSchedulingLink =
    onboardingData?.member?.plan_selection_call_scheduling_links?.en

  if (medicalPlanError) {
    return <PageErrorBanner error={medicalPlanError} />
  }

  if (submitError) {
    return (
      <Banner
        title='A problem has occurred while processing your request'
        type='critical'
      >
        <OnboardingGenericErrorMessage
          error={submitError as GrapeAPIErrorType}
        />
      </Banner>
    )
  }

  // https://thatchhealth.slack.com/archives/C085V7S9UT0/p1757089835083109 for context
  // We will remove this logic once Aetna is no longer in the data
  const hasAetna = !medicalPlanLoading && filterNames.carriers?.includes('Aetna CVS Health')

  const renderContent = () => {
    let planContents

    let loadingComponent = null
    if (medicalPlanLoading) {
      loadingComponent = (
        <div
          data-testid='MedicalPlansPageLoading'
          className='flex items-center justify-center'
        >
          <LoadingSpinnerCentered />
        </div>
      )
    }
    if (recommendationNotAvailable && totalPlans === 0) {
      planContents = (
        <Banner title='No plans found' type='info'>
          {!planSelectionCallSchedulingLink && (
            "It looks like we don't have any plans in your area."
          )}
          {planSelectionCallSchedulingLink && (
            <>
              It looks like we don't have any plans in your area, but you can
              {' '}<TextLink href={planSelectionCallSchedulingLink} target='_blank'>schedule time</TextLink>{' '}
              with one of our licensed insurance experts to find something that works for you.
            </>
          )}
        </Banner>
      )
    } else if (totalPlans > 0) {
      planContents = (
        <>
          <div className='flex items-start justify-between gap-2 mb-4'>
            <div className='flex items-center gap-2 mb-4'>
              <div className='body text-gray-800'>
                {totalPlans} medical plan{totalPlans === 1 ? '' : 's'}
                {city ? ` in ${city}` : ''}
              </div>
              {medicalPlanFetching && <LoadingSpinnerCentered height='20px' width='20px' />}
            </div>
            <DropdownMenu
              trigger={({ open }) => (
                <button
                  type='button'
                  className='flex items-center gap-2 body text-gray-500 hover:text-gray-800 transition-colors rounded-lg outline outline-offset-2 outline-2 outline-transparent focus-visible:outline-thatch-500'
                >
                  <span>Sort: <b className='font-medium'>{getSortLabel(sortArray)}</b></span>
                  {open
                    ? (
                      <ChevronUpIcon size={16} aria-hidden />
                      )
                    : (
                      <ChevronDownIcon size={16} aria-hidden />
                      )}
                </button>
              )}
            >
              {SORT_OPTIONS.map((option) => (
                <DropdownMenu.Item
                  key={option.label}
                  onSelect={() => {
                    updateSort(option.sortArray)
                  }}
                >
                  {option.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu>
          </div>
          <div className='flex flex-col gap-4'>
            {labeledPlans
              .filter(({ plan }) => showThisRecommendedPlan(plan.plan, planFilters))
              .map(({ plan, planBadgeCategories }) => (
                <div key={plan.plan.id}>
                  <MedicalPlanCard
                    badgeCategories={planBadgeCategories}
                    currentPlanEnrollment={currentPlan}
                    onSelectPlan={() => handleSelectPlan(plan)}
                    planDisplayItem={plan}
                    setShowMedicalPlanDetail={setShowMedicalPlanDetail}
                    selectedPlansForComparison={selectedPlansForComparison}
                    onPlanComparisonToggle={handlePlanComparisonToggle}
                    onSelectMorePlansForVerification={onSelectMorePlans}
                  />
                </div>
              ))}
            {availablePlans.length > 0 &&
              availablePlans.map((plan) => {
                // Check if this plan is already shown as a recommendation
                const isRecommendedPlan = labeledPlans.some(
                  ({ plan: recommendedPlan }) => recommendedPlan.plan.id === plan.plan.id
                )
                if (isRecommendedPlan) {
                  return null
                }
                return (
                  <MedicalPlanCard
                    key={plan.plan.id}
                    currentPlanEnrollment={currentPlan}
                    onSelectPlan={() => handleSelectPlan(plan)}
                    planDisplayItem={plan}
                    setShowMedicalPlanDetail={setShowMedicalPlanDetail}
                    selectedPlansForComparison={selectedPlansForComparison}
                    onPlanComparisonToggle={handlePlanComparisonToggle}
                    onSelectMorePlansForVerification={onSelectMorePlans}
                  />
                )
              })}
          </div>
        </>
      )
    } else if (planSelectionCallSchedulingLink) {
      planContents = (
        <Banner title='No plans found' type='info'>
          No plans match your current criteria — try adjusting your filters, or
          {' '}<TextLink href={planSelectionCallSchedulingLink} target='_blank'>set up time</TextLink>{' '}
          with our licensed insurance experts who can help find something that works for you.
        </Banner>
      )
    } else if (!medicalPlanLoading && totalPlans === 0) {
      planContents = (
        <Banner title='No plans found' type='info'>
          No plans match your current criteria — try adjusting your filters.
        </Banner>
      )
    }

    return (
      <>
        {!recommendationNotAvailable && (
          <PlanGranularFilters
            carrierFilterNames={filterNames.carriers}
            metalTierFilterNames={filterNames.metalTiers}
            planTypeFilterNames={filterNames.planTypes}
            filters={planFilters}
            setFilters={setPlanFilters}
          />
        )}
        {loadingComponent}
        {planContents}
        <PlanComparisonSidebarWidget
          selectedPlansForComparison={selectedPlansForComparison}
          onCompareClick={handleCompareClick}
          onClearAll={() => setSelectedPlansForComparison([])}
          onRemovePlan={(plan) => handlePlanComparisonToggle(plan, false)}
        />
      </>
    )
  }

  const shouldShowNevadaIncorrectPricesBanner = nvIncorrectPlanPricesBanner &&
    onboardingData?.member?.state === 'NV' &&
    onboardingData?.enrollment_event.plan_year === 2026

  return (
    <div data-testid='MedicalPlansPage' className='max-w-[44rem]'>
      {/* Banners */}
      {shouldShowNevadaIncorrectPricesBanner && (
        <div className='mb-6'>
          <Banner
            title={
              <>
                Some <span className='body-bold'>Health Plan of Nevada</span> and <span className='body-bold'>Sierra Health and Life</span> plans are showing cheaper prices than intended.
                <br />
                We're working with our data provider on a fix, but, until then, assume that picking one of their plans will cost about 3% more than displayed.
              </>
          }
            type='notice'
          />
        </div>
      )}
      {hasAetna && (
        <div className='mb-6'>
          <Banner
            title={
              <>
                Aetna CVS is <TextLink href='https://www.aetnacvshealth.com/2026-plan-information.html' target='_blank'>leaving the individual market</TextLink>{' '}
                at the end of the year. You can still pick one of their plans now, but won't be able to keep it going into 2026.
              </>
          }
            type='notice'
          />
        </div>
      )}
      {showSmartBudget && (
        <div className='mb-6'>
          <BorderedBanner
            icon={Sparkles}
            text='Need help choosing? Maximize your allowance with Smart Budget.'
            linkText='Try now'
            onLinkClick={() => {
              window.segment?.track('[EE] Smart budget banner clicked')
              navigateToSpecificStep?.('smart_budget')
            }}
          />
        </div>
      )}
      <div className='mb-6'>
        <h1 className='heading-lg text-gray-800 mb-3'>
          Choose your medical insurance
        </h1>
        <p className='body text-gray-500'>
          We rank every plan in your area based on what matters most to your
          health and your wallet — enter a few details and click Search to see recommendations.
        </p>
        <div className='mt-2'>
          <TextButton
            data-testid='MedicalPlansPageByopLink'
            onClick={() => setViewingOffThatch(true)}
          >
            Already have a plan or want to purchase a plan yourself?
          </TextButton>
        </div>
      </div>
      {!recommendationNotAvailable && (
        <div className='mb-4'>
          <RecommendationQuestionBar onSearch={handleRecommendationSearch} />
        </div>
      )}
      {renderContent()}
    </div>
  )
}
